import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {
  ExternalLink,
  UserRound,
} from 'lucide-react'
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate } from 'react-router'

import { NextUpTasksList } from 'components/brokers/workflows/NextUpTasksList'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { SortableHeader } from 'components/reusable/table/SortableHeader'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { Checkbox } from 'components/ui/checkbox'
import { brokersMembers } from 'rails_routes'
import { getLocaleDateString } from 'utils'
import {
  useGetBrokersTaskWorkflows,
} from 'utils/web_api'
import { type BrokersWorkflowList } from 'utils/web_api.schemas'

import AssignBrokersToWorkflow from './AssignBrokersToWorkflow'
import { transformFiltersToParams, type WorkflowsFilters } from './workflowsHelpers'
import { WorkflowStatusDocumentationTrigger } from './WorkflowStatusDocumentationSheet'
import {
  CoverageBadge,
  EnrollmentEventBadge,
  SourceBadge,
  WorkflowStatusBadge,
  WorkflowTypeBadge,
} from './WorkflowVisualIndicators'
import { DisplayTextOnHoverTooltip } from '../needs_attention/NeedsAttentionTable'
import { createAddArrayFilter, createSetSingleValueFilter, getCarrierFilterInfo } from '../tasks/utils'

type WorkflowsTableProps = {
  filters: WorkflowsFilters
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
  columnVisibilityOverrides?: Partial<Record<keyof BrokersWorkflowList, boolean>>
  rowSelection?: RowSelectionState
  setRowSelection?: Dispatch<SetStateAction<RowSelectionState>>
  openInNewTab?: boolean
  evaluateTaskStatus?: boolean
}

const columnHelper = createColumnHelper<BrokersWorkflowList>()

const ProgressBar = ({
  resolvedTasks,
  totalTasks,
  failedTasks,
}: {
  resolvedTasks: number
  totalTasks: number
  failedTasks: number
}) => {
  const resolvedPercentage =
    totalTasks > 0 ? (resolvedTasks / totalTasks) * 100 : 0
  const failedPercentage = totalTasks > 0 ? (failedTasks / totalTasks) * 100 : 0
  const isComplete = resolvedTasks === totalTasks && totalTasks > 0

  return (
    <div className='flex items-center gap-2 min-w-[120px]'>
      <div className='flex-1 bg-gray-200 rounded-full h-2 relative overflow-hidden'>
        <div
          className={`h-2 rounded-full absolute left-0 ${
            isComplete ? 'bg-green-400' : 'bg-blue-500'
          }`}
          style={{ width: `${resolvedPercentage}%` }}
        />
        {failedTasks > 0 && (
          <div
            className='bg-red-500 h-2 rounded-full absolute'
            style={{
              left: `${resolvedPercentage}%`,
              width: `${failedPercentage}%`,
            }}
          />
        )}
      </div>
      <span className='text-sm text-gray-600 whitespace-nowrap'>
        {resolvedTasks}/{totalTasks}
        {failedTasks > 0 && (
          <span className='text-red-500'> ({failedTasks} failed)</span>
        )}
      </span>
    </div>
  )
}

const WorkflowsTable = ({
  filters,
  setFilters,
  pagination,
  setPagination,
  sorting,
  setSorting,
  columnVisibilityOverrides,
  rowSelection = {},
  setRowSelection = () => {},
  openInNewTab = false,
  evaluateTaskStatus = false,
}: WorkflowsTableProps) => {
  const navigate = useNavigate()
  const params = transformFiltersToParams(filters, pagination, sorting, evaluateTaskStatus)
  const { data, isLoading, isError } = useGetBrokersTaskWorkflows(params)

  const [paginationMetadata, setPaginationMetadata] = useState<{
    total_records: number
    current_page: number
    total_pages: number
    next_page: number | null
    prev_page: number | null
  }>()

  // Helper functions for filtering
  const addArrayFilter = useMemo(() => createAddArrayFilter(setFilters), [setFilters])
  const setSingleValueFilter = useMemo(() => createSetSingleValueFilter(setFilters), [setFilters])

  // Memoize the columns to avoid re-rendering the table.
  const columns = useMemo(() => {
    return [
      // Selection column
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)}
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
      }),
      columnHelper.display({
        header: 'Actions',
        cell: ({ row }: { row: Row<BrokersWorkflowList> }) => {
          return (
            <div className='flex items-center gap-4'>
              <DisplayTooltipOnHover text='View workflow details'>
                <div
                  className='cursor-pointer h-full'
                  onClick={(_e) => {
                    const workflowUrl = `/brokers/workflows/${row.original.id}`
                    if (openInNewTab) {
                      window.open(workflowUrl, '_blank')
                    } else {
                      navigate(workflowUrl)
                    }
                  }}
                >
                  <ExternalLink
                    size={20}
                    className='stroke-current text-gray-600'
                  />
                </div>
              </DisplayTooltipOnHover>
              <DisplayTooltipOnHover text='View Broker Portal'>
                <div
                  className='cursor-pointer h-full'
                  onClick={(_e) => {
                    const memberUrl = brokersMembers.show({ id: row.original.enrollment_info?.member_id })
                    window.open(memberUrl, '_blank')
                  }}
                >
                  <UserRound
                    size={20}
                    className='stroke-current text-gray-600'
                  />
                </div>
              </DisplayTooltipOnHover>
            </div>
          )
        },
      }),
      columnHelper.accessor('created_at', {
        header: (info) => (
          <SortableHeader<BrokersWorkflowList> {...info} headerText='Created' />
        ),
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
        enableSorting: true,
      }),
      columnHelper.accessor('brokers', {
        header: 'Assignees',
        cell: ({ getValue, row }) => {
          const { id } = row.original
          return (
            <AssignBrokersToWorkflow
              id={id}
              assignedBrokers={getValue() || []}
            />
          )
        },
      }),
      columnHelper.accessor('enrollment_info.member_name', {
        id: 'member_name',
        header: (info) => (
          <SortableHeader<BrokersWorkflowList> {...info} headerText='Member' />
        ),
        cell: (info) => {
          const memberName = info.getValue()
          const memberId = info.row.original.enrollment_info?.member_id

          if (!memberName) return '-'

          return (
            <span
              className='cursor-pointer hover:underline'
              onClick={(e) => {
                e.stopPropagation()
                if (memberId) {
                  addArrayFilter('member_ids', memberId)
                }
              }}
            >
              {memberName}
            </span>
          )
        },
        enableSorting: true,
      }),
      columnHelper.accessor('enrollment_info.carrier_name', {
        id: 'carrier_name',
        header: (info) => (
          <SortableHeader<BrokersWorkflowList> {...info} headerText='Carrier' />
        ),
        cell: (info) => {
          const carrierName = info.getValue()
          if (!carrierName) return '-'

          return (
            <span
              className='cursor-pointer hover:underline'
              onClick={(e) => {
                e.stopPropagation()
                const { key, value } = getCarrierFilterInfo(carrierName)
                addArrayFilter(key, value)
              }}
            >
              {carrierName}
            </span>
          )
        },
        enableSorting: true,
      }),
      columnHelper.accessor('enrollment_info.employer_name', {
        id: 'employer_dba',
        header: (info) => (
          <SortableHeader<BrokersWorkflowList> {...info} headerText='Employer' />
        ),
        cell: (info) => {
          const employerName = info.getValue()
          const employerId = info.row.original.enrollment_info?.employer_id

          return (
            <span
              onClick={(e) => {
                if (employerId) {
                  e.stopPropagation()
                  addArrayFilter('employer_ids', employerId)
                }
              }}
            >
              <DisplayTextOnHoverTooltip
                text={employerName || 'N/A'}
                classNames={`max-w-40 ${employerId ? 'cursor-pointer hover:underline' : ''}`}
              />
            </span>
          )
        },
        enableSorting: true,
      }),
      columnHelper.accessor('enrollment_info.submitting_brokerage_name', {
        id: 'enrollment_info.brokerage_name',
        header: 'Brokerage',
        cell: (info) => {
          const brokerageName = info.getValue()
          const brokerageId = info.row.original.enrollment_info?.submitting_brokerage_id

          if (!brokerageName) return 'N/A'

          return (
            <span
              className={`max-w-40 truncate block ${brokerageId ? 'cursor-pointer hover:underline' : ''}`}
              onClick={(e) => {
                if (brokerageId) {
                  e.stopPropagation()
                  addArrayFilter('brokerage_ids', brokerageId)
                }
              }}
              title={brokerageName}
            >
              {brokerageName}
            </span>
          )
        },
      }),
      columnHelper.accessor('enrollment_info.coverage_type', {
        id: 'coverage_type',
        header: 'Coverage',
        cell: (info) => {
          const coverageType = info.getValue()

          return (
            <CoverageBadge
              coverageType={coverageType}
              onClick={
                coverageType
                  ? (e: React.MouseEvent) => {
                      e.stopPropagation()
                      addArrayFilter('coverage_types', coverageType)
                    }
                  : undefined
              }
            />
          )
        },
      }),
      columnHelper.accessor('source', {
        id: 'source',
        header: 'Source',
        cell: (info) => {
          const source = info.getValue()

          return (
            <SourceBadge
              source={source}
              onClick={
                source
                  ? (e: React.MouseEvent) => {
                      e.stopPropagation()
                      addArrayFilter('source', source)
                    }
                  : undefined
              }
            />
          )
        },
      }),
      columnHelper.accessor('deadline_date', {
        id: 'deadline_date',
        header: (info) => (
          <SortableHeader<BrokersWorkflowList> {...info} headerText='Deadline / Coverage Start' />
        ),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return '-'

          const displayValue = getLocaleDateString(new Date(value))
          // Extract just the date part (YYYY-MM-DD) from the timestamp
          const dateOnly = value.split('T')[0]

          return (
            <span
              className='cursor-pointer hover:underline'
              onClick={(e) => {
                e.stopPropagation()
                // Set both after and before date to filter for this exact date
                setSingleValueFilter('deadline_date_after', dateOnly)
                setSingleValueFilter('deadline_date_before', dateOnly)
              }}
            >
              {displayValue}
            </span>
          )
        },
        enableSorting: true,
      }),
      columnHelper.accessor('enrollment_event.enrollment_type', {
        id: 'event_type',
        header: (info) => (
          <SortableHeader<BrokersWorkflowList> {...info} headerText='Event Type' />
        ),
        cell: (info) => {
          const enrollmentEvent = info.row.original.enrollment_event

          return (
            <EnrollmentEventBadge
              enrollmentEvent={enrollmentEvent}
              onClick={
                enrollmentEvent?.enrollment_type
                  ? (e: React.MouseEvent) => {
                      e.stopPropagation()
                      if (enrollmentEvent.enrollment_type) {
                        addArrayFilter(
                          'enrollment_event_types',
                          enrollmentEvent.enrollment_type
                        )
                      }
                    }
                  : undefined
              }
            />
          )
        },
        enableSorting: true,
      }),
      columnHelper.accessor('workflow_type', {
        id: 'workflow_type',
        header: 'Type',
        cell: (info) => {
          const workflowType = info.getValue()

          return (
            <WorkflowTypeBadge
              workflowType={workflowType}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                addArrayFilter('workflow_types', workflowType)
              }}
            />
          )
        },
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: (info) => (
          <div className='flex items-center'>
            <SortableHeader<BrokersWorkflowList> {...info} headerText='Status' />
            <WorkflowStatusDocumentationTrigger />
          </div>
        ),
        cell: (info) => {
          const status = info.getValue()

          return (
            <WorkflowStatusBadge
              status={status}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                addArrayFilter('statuses', status)
              }}
            />
          )
        },
        enableSorting: true,
      }),
      columnHelper.display({
        header: 'Progress',
        cell: ({ row: _row }) => {
          // Calculate canceled tasks from tasks_summary if available
          const canceledTasks =
            _row.original.tasks_summary?.filter(
              (task) => task.status === 'canceled'
            ).length || 0
          const resolvedTasks =
            (_row.original.completed_task_count || 0) + canceledTasks
          return (
            <ProgressBar
              resolvedTasks={resolvedTasks}
              totalTasks={_row.original.task_count}
              failedTasks={_row.original.failed_task_count}
            />
          )
        },
      }),
      columnHelper.accessor('started_at', {
        header: (info) => (
          <SortableHeader<BrokersWorkflowList> {...info} headerText='Started' />
        ),
        cell: (info) => {
          const value = info.getValue()
          return value ? getLocaleDateString(new Date(value)) : '-'
        },
        enableSorting: true,
      }),
      columnHelper.accessor('completed_at', {
        header: 'Completed',
        cell: (info) => {
          const value = info.getValue()
          return value ? getLocaleDateString(new Date(value)) : '-'
        },
      }),
      columnHelper.accessor('next_up_tasks', {
        header: 'Next Task(s)',
        cell: (info) => {
          const tasks = info.getValue()
          return <NextUpTasksList tasks={tasks ?? []} maxVisible={3} />
        },
      }),
    ]
  }, [addArrayFilter, setSingleValueFilter, openInNewTab, navigate])

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    state: {
      pagination,
      sorting,
      columnVisibility: columnVisibilityOverrides,
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.id,
    manualPagination: true,
    manualSorting: true,
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      noDataMessage='There are no workflows to display.'
      setSorting={setSorting}
      defaultSortingState={[]}
      paginationLabelSingular='workflow'
      paginationMetadata={paginationMetadata}
      tableMessageHeightClass='h-32'
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 0,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 0,
        })}
    />
  )
}

export default WorkflowsTable
